import { useRef, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';

/**
 * The sheet tabs. Drag one along the strip to reorder.
 *
 * Pointer CAPTURE on the tab, as everywhere else: inside a cross-origin iframe
 * a window-level pointermove never arrives, so a drag wired to window listeners
 * sticks on the first pixel.
 */
export function SheetTabs(): JSX.Element {
  useStore();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [overSlot, setOverSlot] = useState<number | null>(null);
  const stripRef = useRef<HTMLDivElement | null>(null);

  /**
   * The insertion SLOT under the pointer — the gap between tabs, 0..n.
   *
   * Slot n is the gap after the last tab, which an index into the tabs cannot
   * express: without it there is no way to drag a sheet to the end.
   */
  const slotAt = (clientX: number): number => {
    const tabs = tabElements();
    for (let i = 0; i < tabs.length; i++) {
      const box = tabs[i]!.getBoundingClientRect();
      if (clientX < box.left + box.width / 2) return i;
    }
    return tabs.length;
  };

  const tabElements = (): HTMLElement[] =>
    stripRef.current ? [...stripRef.current.querySelectorAll<HTMLElement>('[data-sheet]')] : [];

  /**
   * Where to draw the caret for a slot, in the strip's own scrolled
   * coordinates — the strip scrolls sideways once there are enough sheets.
   */
  const caretX = (slot: number): number | null => {
    const strip = stripRef.current;
    const tabs = tabElements();
    if (!strip || tabs.length === 0) return null;
    const stripBox = strip.getBoundingClientRect();
    const edge =
      slot >= tabs.length
        ? tabs[tabs.length - 1]!.getBoundingClientRect().right
        : tabs[slot]!.getBoundingClientRect().left;
    return edge - stripBox.left + strip.scrollLeft;
  };

  const startDrag = (e: React.PointerEvent<HTMLElement>, id: string): void => {
    if (e.button !== 0) return;
    /*
     * Mid-formula, keep the caret.
     *
     * `preventDefault` on pointerdown stops the browser moving focus, so the
     * cell editor is not blurred — and blur commits. Without it, clicking a
     * sheet tab to pick a cross-sheet reference ends the edit before the pick
     * can be made.
     */
    if (store.editing) e.preventDefault();
    const handle = e.currentTarget;
    let moved = false;
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort; without it the tab simply does not drag */
    }
    const onMove = (ev: PointerEvent): void => {
      // A few pixels of slop, so a click that wobbles is still a click.
      if (!moved && Math.abs(ev.clientX - e.clientX) < 4) return;
      moved = true;
      setDragging(id);
      setOverSlot(slotAt(ev.clientX));
    };
    const onUp = (ev: PointerEvent): void => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      try {
        handle.releasePointerCapture(ev.pointerId);
      } catch {
        /* already released */
      }
      if (moved) store.moveSheetToSlot(id, slotAt(ev.clientX));
      setDragging(null);
      setOverSlot(null);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };


  // Hide the caret where the drop would change nothing — the two gaps either
  // side of the tab being dragged — so it never promises a move that will not
  // happen.
  const draggedIndex = dragging ? store.wb.sheets.findIndex((s) => s.id === dragging) : -1;
  const showCaret =
    dragging !== null && overSlot !== null && overSlot !== draggedIndex && overSlot !== draggedIndex + 1;
  const caretLeft = showCaret ? caretX(overSlot!) : null;

  // The system tabs (Areas, Features, Blocks, Usages, Analytics — plus a
  // legacy Measures sheet, left behind inert on a workbook where something
  // still references it, see store.ts's `migrateAwayMeasuresSheet`) are
  // pinned as one contiguous block at the end — store.ts keeps them there.
  // The Add button sits at the boundary, so a new ordinary sheet always
  // lands beside the other ordinary ones, never inside or past the pinned
  // block.
  // Analytics is dev-mode-only (store.ts's `toggleDevMode` moves off it when
  // dev mode turns off, so there is never an active sheet with no tab here).
  const sheets = store.devMode ? store.wb.sheets : store.wb.sheets.filter((s) => s.role !== 'analytics');
  const boundaryIndex = sheets.findIndex((s) => s.role !== undefined);
  const boundary = boundaryIndex < 0 ? sheets.length : boundaryIndex;

  const renderTab = (sheet: (typeof sheets)[number]): JSX.Element => {
    const active = sheet.id === store.wb.activeSheetId;
    const system = sheet.role !== undefined;

    if (renaming === sheet.id && !system) {
      return (
        <input
          key={sheet.id}
          className="gt-tab-input"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            store.renameSheet(sheet.id, draft);
            setRenaming(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              store.renameSheet(sheet.id, draft);
              setRenaming(null);
            } else if (e.key === 'Escape') {
              setRenaming(null);
            }
          }}
        />
      );
    }

    return (
      <button
        key={sheet.id}
        data-sheet={sheet.id}
        className={[
          'gt-tab',
          active ? 'is-active' : '',
          system ? 'is-system' : '',
          dragging === sheet.id ? 'is-dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onPointerDown={(e) => startDrag(e, sheet.id)}
        onClick={() => store.activateSheet(sheet.id)}
        onDoubleClick={() => {
          if (system) return;
          setRenaming(sheet.id);
          setDraft(sheet.name);
        }}
        title={
          system
            ? 'The project usage table. Edit a rate here and push it back to Giraffe. This sheet cannot be deleted or renamed. Drag to reorder.'
            : 'Double-click to rename, drag to reorder'
        }
      >
        {system ? <span className="gt-tab-mark">⟳</span> : null}
        {sheet.name}
        {!system ? (
          <span
            className="gt-tab-close"
            role="button"
            tabIndex={-1}
            title={`Delete ${sheet.name} (Cmd/Ctrl+Z to undo)`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              store.deleteSheet(sheet.id);
            }}
          >
            ×
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <div className="gt-tabs" ref={stripRef}>
      {caretLeft !== null ? <div className="gt-tab-caret" style={{ left: caretLeft }} /> : null}
      {sheets.slice(0, boundary).map(renderTab)}
      <button className="gt-tab gt-tab-add" onClick={() => store.addSheet()} title="Add sheet">
        +
      </button>
      {sheets.slice(boundary).map(renderTab)}
    </div>
  );
}
